// src/utils/logoCacheUtils.js
/**
 * Utility for caching token logos in localStorage to improve performance
 * and reduce network requests
 */

// Default token logo as data URI for fallback
export const DEFAULT_TOKEN_LOGO = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='50' height='50'%3E%3Ccircle cx='25' cy='25' r='20' fill='%23e0e0e0'/%3E%3Ctext x='25' y='30' font-family='Arial' font-size='20' text-anchor='middle' fill='%23707070'%3E?%3C/text%3E%3C/svg%3E";

// Cache keys
const LOGO_CACHE_KEY_PREFIX = 'bitdog_logo_cache_';
const LOGO_CACHE_TIMESTAMP_PREFIX = 'bitdog_logo_timestamp_';
const LOGO_CACHE_EXPIRY = 60 * 24 * 60 * 60 * 1000; // 60 days in milliseconds

/**
 * Get a cached logo from localStorage
 * @param {string} tokenAddress - The token address
 * @param {string} chainKey - The chain identifier
 * @returns {string|null} - The cached logo URL or null if not cached
 */
export const getCachedLogo = (tokenAddress, chainKey) => {
  if (!tokenAddress) return null;
  
  const cacheKey = `${LOGO_CACHE_KEY_PREFIX}${chainKey}_${tokenAddress.toLowerCase()}`;
  const timestampKey = `${LOGO_CACHE_TIMESTAMP_PREFIX}${chainKey}_${tokenAddress.toLowerCase()}`;
  
  try {
    // Get cached data
    const cachedLogoUrl = localStorage.getItem(cacheKey);
    const cachedTimestamp = localStorage.getItem(timestampKey);
    
    // If no cached data, return null
    if (!cachedLogoUrl || !cachedTimestamp) return null;
    
    // Check if cache has expired
    const timestamp = parseInt(cachedTimestamp, 10);
    const now = Date.now();
    
    if (now - timestamp > LOGO_CACHE_EXPIRY) {
      // Cache expired, clean up and return null
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(timestampKey);
      return null;
    }
    
    // Return cached logo URL
    return cachedLogoUrl;
  } catch (error) {
    console.error('Error accessing logo cache:', error);
    return null;
  }
};

/**
 * Cache a logo URL in localStorage
 * @param {string} tokenAddress - The token address
 * @param {string} chainKey - The chain identifier
 * @param {string} logoUrl - The logo URL to cache
 */
export const cacheLogo = (tokenAddress, chainKey, logoUrl) => {
  if (!tokenAddress || !logoUrl) return;
  
  // Skip caching data URIs
  if (logoUrl.startsWith('data:')) return;
  
  const cacheKey = `${LOGO_CACHE_KEY_PREFIX}${chainKey}_${tokenAddress.toLowerCase()}`;
  const timestampKey = `${LOGO_CACHE_TIMESTAMP_PREFIX}${chainKey}_${tokenAddress.toLowerCase()}`;
  
  try {
    localStorage.setItem(cacheKey, logoUrl);
    localStorage.setItem(timestampKey, Date.now().toString());
  } catch (error) {
    console.error('Error caching logo:', error);
    
    // If storage is full, clear old cache entries
    if (error.name === 'QuotaExceededError') {
      clearOldLogoCacheEntries();
      
      // Try again
      try {
        localStorage.setItem(cacheKey, logoUrl);
        localStorage.setItem(timestampKey, Date.now().toString());
      } catch (retryError) {
        console.error('Failed to cache logo after clearing old entries:', retryError);
      }
    }
  }
};

/**
 * Prefetch and cache multiple logos
 * @param {Array} tokens - Array of token objects with addresses
 * @param {string} chainKey - The chain identifier
 * @param {Object} logoUrlMap - Map of token addresses to logo URLs
 */
export const prefetchLogos = async (tokens, chainKey, logoUrlMap) => {
  if (!tokens || !tokens.length) return;
  
  const prefetchPromises = tokens
    .filter(token => token.token && token.token.address && logoUrlMap[token.token.address.toLowerCase()])
    .map(async (token) => {
      const address = token.token.address.toLowerCase();
      const logoUrl = logoUrlMap[address];
      
      // Skip if already cached and not expired
      if (getCachedLogo(address, chainKey)) return;
      
      try {
        // For image URLs (not data URIs), prefetch and cache
        if (logoUrl && !logoUrl.startsWith('data:')) {
          // Test if the image loads successfully
          const img = new Image();
          img.onload = () => cacheLogo(address, chainKey, logoUrl);
          img.onerror = () => console.warn(`Failed to load logo for ${address}`);
          img.src = logoUrl;
        }
      } catch (error) {
        console.error(`Error prefetching logo for ${address}:`, error);
      }
    });
  
  // Run prefetching in the background
  Promise.all(prefetchPromises).catch(err => {
    console.error('Error during logo prefetching:', err);
  });
};

/**
 * Clear old cache entries when storage is full
 * Removes the oldest 50% of entries
 */
export const clearOldLogoCacheEntries = () => {
  try {
    const cacheEntries = [];
    
    // Collect all logo cache entries
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith(LOGO_CACHE_KEY_PREFIX)) {
        const timestampKey = key.replace(LOGO_CACHE_KEY_PREFIX, LOGO_CACHE_TIMESTAMP_PREFIX);
        const timestamp = parseInt(localStorage.getItem(timestampKey), 10) || 0;
        cacheEntries.push({ logoKey: key, timestampKey, timestamp });
      }
    }
    
    // Sort by timestamp (oldest first)
    cacheEntries.sort((a, b) => a.timestamp - b.timestamp);
    
    // Remove the oldest 50% of entries
    const entriesToRemove = cacheEntries.slice(0, Math.ceil(cacheEntries.length / 2));
    entriesToRemove.forEach(entry => {
      localStorage.removeItem(entry.logoKey);
      localStorage.removeItem(entry.timestampKey);
    });
    
    console.log(`Cleared ${entriesToRemove.length} old logo cache entries`);
  } catch (error) {
    console.error('Error clearing old cache entries:', error);
  }
};

/**
 * Get a logo URL, with cache support
 * @param {string} tokenAddress - The token address
 * @param {string} chainKey - The chain identifier
 * @param {string} logoUrl - The fallback logo URL
 * @returns {string} - The logo URL (cached or fallback)
 */
export const getLogoUrl = (tokenAddress, chainKey, logoUrl) => {
  if (!tokenAddress) return DEFAULT_TOKEN_LOGO;
  
  // Check cache first
  const cachedLogo = getCachedLogo(tokenAddress, chainKey);
  if (cachedLogo) return cachedLogo;
  
  // If not cached, use the provided URL and cache it
  if (logoUrl && !logoUrl.startsWith('data:')) {
    cacheLogo(tokenAddress, chainKey, logoUrl);
  }
  
  return logoUrl || DEFAULT_TOKEN_LOGO;
};

/**
 * Hook to use token logos with caching
 * @param {Array} tokens - Array of token objects
 * @param {string} chainKey - The chain identifier 
 * @param {Object} fetchedLogos - Map of token addresses to logo URLs
 * @returns {Object} - Map of token addresses to logo URLs (from cache if available)
 */
export const useLogoCache = (tokens, chainKey, fetchedLogos = {}) => {
  // Initialize with cached logos where available
  const cachedLogos = {};
  
  // Process every token
  if (tokens && tokens.length) {
    tokens.forEach(tokenObj => {
      if (tokenObj.token && tokenObj.token.address) {
        const address = tokenObj.token.address.toLowerCase();
        
        // Check cache first
        const cachedLogo = getCachedLogo(address, chainKey);
        if (cachedLogo) {
          cachedLogos[address] = cachedLogo;
        } else if (fetchedLogos[address]) {
          // If not in cache but in fetched logos, cache it
          cacheLogo(address, chainKey, fetchedLogos[address]);
          cachedLogos[address] = fetchedLogos[address];
        }
      }
    });
  }
  
  // Prefetch in background
  prefetchLogos(tokens, chainKey, fetchedLogos);
  
  return cachedLogos;
};